import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const RiskDisclosurePage = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 py-20">
      <SEO title="Risk Disclosure" description="Futures and forex trading risk disclosure for NinjaCopia users." />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="prose prose-lg dark:prose-invert max-w-none"
        >
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">
            Risk Disclosure Statement
          </h1>

          <div className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            Last updated: January 2025
          </div>

          <div className="space-y-8">
            <section>
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 mb-6">
                <p className="text-red-800 dark:text-red-200 font-bold text-lg mb-4">
                  IMPORTANT RISK DISCLOSURE
                </p>
                <p className="text-red-700 dark:text-red-300">
                  Trading futures and forex involves substantial risk of loss and is not suitable for all investors. Past performance is not indicative of future results. You should carefully consider whether trading is suitable for you in light of your circumstances, knowledge, and financial resources.
                </p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">General Risk Warning</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                The risk of loss in trading commodity futures contracts and foreign currency can be substantial. You should therefore carefully consider whether such trading is suitable for you in light of your financial condition. In considering whether to trade, you should be aware of the following:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-3 ml-4">
                <li>You may sustain a total loss of the initial margin funds and any additional funds that you deposit with your broker to establish or maintain a position in the commodity futures market.</li>
                <li>If the market moves against your position, you may be called upon by your broker to deposit a substantial amount of additional margin funds, on short notice, in order to maintain your position.</li>
                <li>If you do not provide the required funds within the prescribed time, your position may be liquidated at a loss, and you may be liable for any resulting deficit in your account.</li>
                <li>Under certain market conditions, you may find it difficult or impossible to liquidate a position. This can occur, for example, when the market makes a "limit move."</li>
                <li>The placement of contingent orders by you or your trading advisor, such as a "stop-loss" or "stop-limit" order, will not necessarily limit your losses to the intended amounts, since market conditions may make it impossible to execute such orders.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Leverage and Margin</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                The high degree of leverage that is often obtainable in futures trading can work against you as well as for you. The use of leverage can lead to large losses as well as gains. You could lose more than your initial investment.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Software Disclaimer</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">
                NinjaCopia is trade copying software designed to replicate trades across multiple accounts. While we strive for accuracy and reliability:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-2 ml-4">
                <li>The software does not guarantee execution of any trades.</li>
                <li>Network latency, broker limitations, and other factors may affect trade replication.</li>
                <li>You are solely responsible for monitoring your accounts and trading activity.</li>
                <li>The software is not a substitute for professional trading advice or money management.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No Investment Advice</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                NinjaCopia and its operators do not provide investment advice, trading recommendations, or signals. We provide software tools only. Any trading decisions you make are your own responsibility. You should consult with a qualified financial advisor before making any trading decisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Regulatory Information</h2>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                For more information on the risks of trading futures and options, please visit the{' '}
                <a
                  href="https://www.cftc.gov/ConsumerProtection/index.htm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  CFTC Consumer Protection
                </a>{' '}
                page or the{' '}
                <a
                  href="https://www.nfa.futures.org/investors/investor-resources/index.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  NFA Investor Resources
                </a>{' '}
                page.
              </p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default RiskDisclosurePage;
